import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Announcement, Button, Error as ErrorView, Layout, Loader, Select } from '@utilities';

import FiltersPanel, { getQuotaGroupValue } from './components/FiltersPanel';
import ItemsPanel, { type TurfItemWithReach } from './components/ItemsPanel';
import ResultsPanel from './components/ResultsPanel';
import { SEARCH_PARAM_KEYS } from '../../../../utilities/constants';
import {
  adaptRawDataForTurfQuestion,
  getRawDataTable,
  getTurfQuestions,
  normalizeComparableText,
  type RawDataEntry,
  type TurfAdapterResult,
  type TurfQuestion,
} from './utilities/adapter';
import { calcReach, computeReachCurve, optimizeBasket } from './utilities/algorithm';
import { buildComparisonDimensions, buildComparisonKeyFindings } from './utilities/comparison';
import { buildTurfCsv, downloadCsv, sanitizeCsvFilename } from './utilities/exportCsv';
import {
  buildUsableDemographicFilters,
  filterTurfRespondents,
  getMissingDemographicFilterIds,
  getSelectedDemographicFilters,
  getSelectedQuotaGroups,
  type GlobalDemographicFilter,
  hasActiveTurfFilters,
  type QuotaGroup,
  type UsableDemographicFilter,
} from './utilities/filters';
import {
  clampTurfMaxSize,
  getBooleanParam,
  getDelimitedParam,
  getTurfMaxSize,
  getTurfMaxSizeLimit,
  setBooleanParam,
  setDelimitedParam,
} from './utilities/queryParams';
import {
  clearBasketState,
  getBasketStateFromParam,
  getBasketStateParam,
  toggleBasketItem,
  togglePinnedItem,
  TURF_BASKET_STATES,
  type TurfBasketStateValue,
} from './utilities/state';

import styles from './_index.module.scss';

interface ApiError {
  response?: {
    status?: number | string;
  };
}

interface GlobalFilters {
  demographicFilters?: GlobalDemographicFilter[];
  quotaGroups?: QuotaGroup[];
}

interface SelectOption {
  label: string;
  value: string;
}

interface TurfSimulatorProps {
  globalFilters?: GlobalFilters;
  globalFiltersError?: ApiError;
  isFiltersLoading?: boolean;
  isRawDataLoading?: boolean;
  onRetryRawData?: () => void;
  rawData?: RawDataEntry[];
  rawDataLoadingError?: ApiError;
  surveyQuestions?: TurfQuestion[];
}

const getErrorStatus = (error?: ApiError): number | string | undefined => error?.response?.status;
const isServerError = (error?: ApiError): boolean => Number(getErrorStatus(error)) >= 500;

const TurfSimulator = ({
  globalFilters,
  globalFiltersError,
  isFiltersLoading,
  isRawDataLoading,
  onRetryRawData,
  rawData,
  rawDataLoadingError,
  surveyQuestions = [],
}: TurfSimulatorProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawDataTable = useMemo(() => getRawDataTable(rawData), [rawData]);
  const turfQuestions = useMemo(() => getTurfQuestions(surveyQuestions), [surveyQuestions]);
  const turfQuestionOptions = useMemo(
    () =>
      turfQuestions.map((question) => ({
        label: getTurfQuestionLabel(question),
        value: getTurfQuestionKey(question),
      })),
    [turfQuestions]
  );
  const selectedTurfQuestion = useMemo(() => {
    const selectedQuestionKey = searchParams.get(SEARCH_PARAM_KEYS.TURF_QUESTION);

    return (
      turfQuestions.find((question) => getTurfQuestionKey(question) === selectedQuestionKey) ??
      turfQuestions[0]
    );
  }, [searchParams, turfQuestions]);
  const selectedTurfQuestionOption =
    turfQuestionOptions.find(({ value }) => value === getTurfQuestionKey(selectedTurfQuestion)) ??
    turfQuestionOptions[0];
  const adapterResult = useMemo<TurfAdapterResult | undefined>(() => {
    if (!selectedTurfQuestion) return undefined;

    return adaptRawDataForTurfQuestion({
      question: selectedTurfQuestion,
      rawData,
    });
  }, [rawData, selectedTurfQuestion]);
  const items = adapterResult?.items ?? [];
  const validItemIds = useMemo(() => new Set(items.map(({ id }) => id)), [items]);
  const pinnedItemIds = useMemo(
    () =>
      getDelimitedParam(searchParams, SEARCH_PARAM_KEYS.TURF_PINS).filter((id) =>
        validItemIds.has(id)
      ),
    [searchParams, validItemIds]
  );
  const basketItemIds = useMemo(() => {
    const queryBasketItemIds = getDelimitedParam(searchParams, SEARCH_PARAM_KEYS.TURF_ITEMS).filter(
      (id) => validItemIds.has(id)
    );

    return Array.from(new Set([...queryBasketItemIds, ...pinnedItemIds]));
  }, [pinnedItemIds, searchParams, validItemIds]);
  const includeSpecialItems = getBooleanParam(searchParams, SEARCH_PARAM_KEYS.TURF_INCLUDE_SPECIAL);
  const basketState = basketItemIds.length
    ? getBasketStateFromParam(searchParams.get(SEARCH_PARAM_KEYS.TURF_BASKET_STATE))
    : TURF_BASKET_STATES.CUSTOM;
  const maxSizeLimit = getTurfMaxSizeLimit(items.length);
  const maxSize = getTurfMaxSize(searchParams, SEARCH_PARAM_KEYS.TURF_MAX_SIZE, items.length);
  const optimizerCandidateCount = useMemo(
    () => items.filter((item) => includeSpecialItems || !item.special).length,
    [includeSpecialItems, items]
  );
  const usableDemographicFilters = useMemo(
    () =>
      buildUsableDemographicFilters({
        columns: rawDataTable?.columns,
        demographicFilters: globalFilters?.demographicFilters,
      }),
    [globalFilters?.demographicFilters, rawDataTable?.columns]
  );
  const selectedDemographicFilters = useMemo(
    () =>
      getSelectedDemographicFilters({
        filters: usableDemographicFilters,
        searchParams,
      }),
    [searchParams, usableDemographicFilters]
  );
  const selectedQuotaGroups = useMemo(() => getSelectedQuotaGroups(searchParams), [searchParams]);
  const filteredRespondents = useMemo(
    () =>
      filterTurfRespondents({
        demographicFilters: usableDemographicFilters,
        quotaGroups: globalFilters?.quotaGroups,
        respondents: adapterResult?.respondents,
        selectedDemographicFilters,
        selectedQuotaGroups,
      }),
    [
      adapterResult?.respondents,
      globalFilters?.quotaGroups,
      selectedDemographicFilters,
      selectedQuotaGroups,
      usableDemographicFilters,
    ]
  );
  const itemReachById = useMemo(
    () => new Map(items.map((item) => [item.id, calcReach(filteredRespondents, [item.id])])),
    [filteredRespondents, items]
  );
  const visibleItemsWithReach = useMemo<TurfItemWithReach[]>(() => {
    const sourceIndexByItemId = new Map(items.map((item, index) => [item.id, index]));

    return items
      .filter(
        (item) =>
          !item.special ||
          includeSpecialItems ||
          basketItemIds.includes(item.id) ||
          pinnedItemIds.includes(item.id)
      )
      .map((item) => ({
        item,
        reach: itemReachById.get(item.id) ?? calcReach([], []),
      }))
      .sort((a, b) => {
        const reachDifference = b.reach.hit - a.reach.hit;

        if (reachDifference !== 0) return reachDifference;

        return (
          (sourceIndexByItemId.get(a.item.id) ?? 0) - (sourceIndexByItemId.get(b.item.id) ?? 0)
        );
      });
  }, [basketItemIds, includeSpecialItems, itemReachById, items, pinnedItemIds]);
  const basketItems = useMemo(
    () => items.filter((item) => basketItemIds.includes(item.id)),
    [basketItemIds, items]
  );
  const basketReach = useMemo(
    () => calcReach(filteredRespondents, basketItemIds),
    [basketItemIds, filteredRespondents]
  );
  const reachCurveRows = useMemo(
    () =>
      computeReachCurve({
        items: basketItems,
        pinnedItemIds,
        respondents: filteredRespondents,
      }),
    [basketItems, filteredRespondents, pinnedItemIds]
  );
  const itemReachRows = useMemo(
    () =>
      items.map((item) => ({
        item,
        reach: itemReachById.get(item.id) ?? calcReach([], []),
      })),
    [itemReachById, items]
  );
  const comparisonDimensions = useMemo(
    () =>
      buildComparisonDimensions({
        basketItemIds,
        columns: rawDataTable?.columns,
        demographicFilters: globalFilters?.demographicFilters,
        quotaGroups: globalFilters?.quotaGroups,
        respondents: filteredRespondents,
      }),
    [
      basketItemIds,
      filteredRespondents,
      globalFilters?.demographicFilters,
      globalFilters?.quotaGroups,
      rawDataTable?.columns,
    ]
  );
  const comparisonKeyFindings = useMemo(
    () => buildComparisonKeyFindings({ dimensions: comparisonDimensions }),
    [comparisonDimensions]
  );
  const filterSummary = useMemo(
    () =>
      getFilterSummary({
        quotaGroups: globalFilters?.quotaGroups,
        selectedDemographicFilters,
        selectedQuotaGroups,
        usableDemographicFilters,
      }),
    [
      globalFilters?.quotaGroups,
      selectedDemographicFilters,
      selectedQuotaGroups,
      usableDemographicFilters,
    ]
  );
  const hasActiveFilters = hasActiveTurfFilters({
    selectedDemographicFilters,
    selectedQuotaGroups,
  });

  useEffect(() => {
    const missingDemographicFilterIds = getMissingDemographicFilterIds({
      columns: rawDataTable?.columns,
      demographicFilters: globalFilters?.demographicFilters,
    });

    if (missingDemographicFilterIds.length) {
      console.warn('[TurfSimulator] Hiding demographic filters missing from raw data:', {
        missingDemographicFilterIds,
      });
    }
  }, [globalFilters?.demographicFilters, rawDataTable?.columns]);

  useEffect(() => {
    if (isRawDataLoading || !selectedTurfQuestion || !rawDataTable || items.length) return;

    console.warn('[TurfSimulator] Could not map TURF question to raw data columns:', {
      availableRawAccessors: rawDataTable.columns?.map(({ accessor }) => accessor),
      questionId: selectedTurfQuestion.id,
      questionText: getTurfQuestionLabel(selectedTurfQuestion),
    });
  }, [isRawDataLoading, items.length, rawDataTable, selectedTurfQuestion]);

  if (rawDataLoadingError) {
    return (
      <Layout.Flex.Column gap="large">
        <ErrorView status={getErrorStatus(rawDataLoadingError)} />
        {isServerError(rawDataLoadingError) && onRetryRawData && (
          <Button
            data-testid="turf-simulator-retry-data-button"
            onClick={onRetryRawData}
            text="Retry"
            variant="primary"
          />
        )}
      </Layout.Flex.Column>
    );
  }
  if (globalFiltersError) return <ErrorView status={getErrorStatus(globalFiltersError)} />;
  if (isFiltersLoading || isRawDataLoading) return <Loader isCentered />;

  if (!selectedTurfQuestion) {
    return (
      <Announcement
        header="No TURF question"
        text="This survey does not include a TURF-enabled question."
        variant="info"
      />
    );
  }

  if (!rawDataTable?.data?.length) {
    return (
      <Announcement
        header="No respondent-level raw data"
        text="No respondent-level raw data is available for this survey."
        variant="info"
      />
    );
  }

  if (!items.length) {
    return (
      <Announcement
        header="TURF data unavailable"
        text="TURF Simulator could not find respondent-level data for this question."
        variant="warn"
      />
    );
  }

  const handleQuestionChange = (option: unknown) => {
    const selectedOption = option as null | SelectOption;

    if (!selectedOption?.value) return;

    const params = new URLSearchParams(searchParams);
    params.set(SEARCH_PARAM_KEYS.TURF_QUESTION, selectedOption.value);
    params.delete(SEARCH_PARAM_KEYS.TURF_ITEMS);
    params.delete(SEARCH_PARAM_KEYS.TURF_PINS);
    params.delete(SEARCH_PARAM_KEYS.TURF_BASKET_STATE);
    setSearchParams(params, { preventScrollReset: true });
  };

  const handleDemographicFilterChange = (
    filter: UsableDemographicFilter,
    value: Record<string, boolean>
  ) => {
    const params = new URLSearchParams(searchParams);
    const selectedValues = Object.keys(value).filter((key) => value[key]);
    setDelimitedParam(params, filter.id, selectedValues);
    params.delete(SEARCH_PARAM_KEYS.TURF_BASKET_STATE);
    setSearchParams(params, { preventScrollReset: true });
  };

  const handleQuotaGroupChange = (group: QuotaGroup) => {
    const groupValue = getQuotaGroupValue(group);

    if (!groupValue) return;

    const params = new URLSearchParams(searchParams);
    const allGroupValues =
      globalFilters?.quotaGroups?.map(getQuotaGroupValue).filter(Boolean) ?? [];
    const currentSelectedGroupValues =
      selectedQuotaGroups === null ? allGroupValues : [...selectedQuotaGroups];
    const nextSelectedGroupValues = currentSelectedGroupValues.includes(groupValue)
      ? currentSelectedGroupValues.filter((value) => value !== groupValue)
      : [...currentSelectedGroupValues, groupValue];

    if (nextSelectedGroupValues.length === allGroupValues.length) {
      params.delete(SEARCH_PARAM_KEYS.QUOTA_GROUP);
    } else {
      params.set(SEARCH_PARAM_KEYS.QUOTA_GROUP, nextSelectedGroupValues.join(','));
    }

    params.delete(SEARCH_PARAM_KEYS.TURF_BASKET_STATE);
    setSearchParams(params, { preventScrollReset: true });
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete(SEARCH_PARAM_KEYS.QUOTA_GROUP);
    usableDemographicFilters.forEach(({ id }) => params.delete(id));
    params.delete(SEARCH_PARAM_KEYS.TURF_BASKET_STATE);
    setSearchParams(params, { preventScrollReset: true });
  };

  const setBasketState = ({
    basketItemIds: nextBasketItemIds,
    basketState: nextBasketState = TURF_BASKET_STATES.CUSTOM,
    pinnedItemIds: nextPinnedItemIds,
  }: {
    basketItemIds: string[];
    basketState?: TurfBasketStateValue;
    pinnedItemIds: string[];
  }) => {
    const params = new URLSearchParams(searchParams);
    setDelimitedParam(params, SEARCH_PARAM_KEYS.TURF_ITEMS, nextBasketItemIds);
    setDelimitedParam(params, SEARCH_PARAM_KEYS.TURF_PINS, nextPinnedItemIds);
    if (nextBasketState === TURF_BASKET_STATES.BEST_SET) {
      params.set(SEARCH_PARAM_KEYS.TURF_BASKET_STATE, getBasketStateParam(nextBasketState));
    } else {
      params.delete(SEARCH_PARAM_KEYS.TURF_BASKET_STATE);
    }
    setSearchParams(params, { preventScrollReset: true });
  };

  const handleBasketItemToggle = (itemId: string) => {
    setBasketState(
      toggleBasketItem({
        basketItemIds,
        itemId,
        pinnedItemIds,
      })
    );
  };

  const handlePinnedItemToggle = (itemId: string) => {
    setBasketState({
      ...togglePinnedItem({
        basketItemIds,
        itemId,
        pinnedItemIds,
      }),
      basketState: basketItemIds.includes(itemId) ? basketState : TURF_BASKET_STATES.CUSTOM,
    });
  };

  const handleClearBasket = () => setBasketState(clearBasketState());

  const handleFindBestSet = () => {
    const optimizedBasket = optimizeBasket({
      items,
      options: {
        includeSpecialItems,
        maxSize,
        pinnedItemIds,
      },
      respondents: filteredRespondents,
    });

    setBasketState({
      basketItemIds: optimizedBasket.itemIds,
      basketState: TURF_BASKET_STATES.BEST_SET,
      pinnedItemIds: pinnedItemIds.filter((itemId) => optimizedBasket.itemIds.includes(itemId)),
    });
  };

  const handleIncludeSpecialItemsChange = (value: boolean) => {
    const params = new URLSearchParams(searchParams);
    setBooleanParam(params, SEARCH_PARAM_KEYS.TURF_INCLUDE_SPECIAL, value);
    params.delete(SEARCH_PARAM_KEYS.TURF_BASKET_STATE);
    setSearchParams(params, { preventScrollReset: true });
  };

  const handleMaxSizeChange = (value: number) => {
    const params = new URLSearchParams(searchParams);
    params.set(SEARCH_PARAM_KEYS.TURF_MAX_SIZE, String(clampTurfMaxSize(value, items.length)));
    params.delete(SEARCH_PARAM_KEYS.TURF_BASKET_STATE);
    setSearchParams(params, { preventScrollReset: true });
  };

  const handleExportCsv = () => {
    const questionLabel = getTurfQuestionLabel(selectedTurfQuestion);
    const csv = buildTurfCsv({
      basketState,
      comparisonDimensions,
      completedRespondentCount: adapterResult?.completeRowCount ?? 0,
      exportTimestamp: new Date().toISOString(),
      filteredRespondentCount: filteredRespondents.length,
      filterSummary,
      includeSpecialItems,
      itemReachRows,
      maxSize,
      questionLabel,
      reachCurveRows,
    });

    downloadCsv({
      csv,
      filename: `${sanitizeCsvFilename(questionLabel || 'turf-simulator')}-turf-simulator.csv`,
    });
  };

  return (
    <Layout.Flex.Column className={styles['turf-simulator']} gap="large">
      {turfQuestions.length > 1 ? (
        <Select
          className={styles['turf-simulator-question-select']}
          data-testid="turf-simulator-question-select"
          label="TURF Question"
          onChange={handleQuestionChange}
          options={turfQuestionOptions}
          value={selectedTurfQuestionOption}
        />
      ) : (
        <div className={styles['turf-simulator-question']}>
          <span>TURF Question</span>
          <strong>{getTurfQuestionLabel(selectedTurfQuestion)}</strong>
        </div>
      )}

      <Layout.Flex className={styles['turf-simulator-layout']} gap="large">
        <Layout.Sidebar className={styles['turf-simulator-sidebar']}>
          <FiltersPanel
            completeRespondentCount={adapterResult?.completeRowCount ?? 0}
            filteredRespondentCount={filteredRespondents.length}
            hasActiveFilters={hasActiveFilters}
            onDemographicFilterChange={handleDemographicFilterChange}
            onQuotaGroupChange={handleQuotaGroupChange}
            onResetFilters={handleResetFilters}
            quotaGroups={globalFilters?.quotaGroups}
            selectedDemographicFilters={selectedDemographicFilters}
            selectedQuotaGroups={selectedQuotaGroups}
            usableDemographicFilters={usableDemographicFilters}
          />
        </Layout.Sidebar>

        <Layout.Fill>
          <div className={styles['turf-simulator-main']}>
            <ItemsPanel
              basketItemIds={basketItemIds}
              basketState={basketState}
              filteredRespondentCount={filteredRespondents.length}
              includeSpecialItems={includeSpecialItems}
              isOptimizerDisabled={
                filteredRespondents.length === 0 || optimizerCandidateCount === 0
              }
              items={visibleItemsWithReach}
              maxSize={maxSize}
              maxSizeLimit={maxSizeLimit}
              onBasketItemToggle={handleBasketItemToggle}
              onClearBasket={handleClearBasket}
              onFindBestSet={handleFindBestSet}
              onIncludeSpecialItemsChange={handleIncludeSpecialItemsChange}
              onMaxSizeChange={handleMaxSizeChange}
              onPinnedItemToggle={handlePinnedItemToggle}
              pinnedItemIds={pinnedItemIds}
            />
            <ResultsPanel
              basketItems={basketItems}
              basketState={basketState}
              comparisonDimensions={comparisonDimensions}
              completeRespondentCount={adapterResult?.completeRowCount ?? 0}
              filteredRespondentCount={filteredRespondents.length}
              keyFindings={comparisonKeyFindings}
              onExportCsv={handleExportCsv}
              reach={basketReach}
              reachCurveRows={reachCurveRows}
            />
          </div>
        </Layout.Fill>
      </Layout.Flex>
    </Layout.Flex.Column>
  );
};

const getTurfQuestionKey = (question?: TurfQuestion): string => {
  if (!question) return '';
  if (question.id != null) return String(question.id);

  return normalizeComparableText(getTurfQuestionLabel(question));
};

const getTurfQuestionLabel = (question: TurfQuestion): string =>
  String(question.text ?? question.title ?? question.richText ?? '').trim();

const getFilterSummary = ({
  quotaGroups = [],
  selectedDemographicFilters,
  selectedQuotaGroups,
  usableDemographicFilters,
}: {
  quotaGroups?: QuotaGroup[];
  selectedDemographicFilters: Record<string, string[]>;
  selectedQuotaGroups: null | string[];
  usableDemographicFilters: UsableDemographicFilter[];
}): string => {
  const summaryParts: string[] = [];

  if (selectedQuotaGroups !== null) {
    const quotaGroupLabelByValue = new Map(
      quotaGroups.map((group) => [
        getQuotaGroupValue(group),
        group.name ?? getQuotaGroupValue(group),
      ])
    );

    summaryParts.push(
      `Quota groups: ${
        selectedQuotaGroups.map((value) => quotaGroupLabelByValue.get(value) ?? value).join('; ') ||
        'None'
      }`
    );
  }

  usableDemographicFilters.forEach((filter) => {
    const selectedValues = selectedDemographicFilters[filter.id] ?? [];

    if (!selectedValues.length) return;

    const optionLabelById = new Map(
      filter.options.map((option) => [String(option.id), option.label])
    );
    summaryParts.push(
      `${filter.label}: ${selectedValues.map((value) => optionLabelById.get(value) ?? value).join('; ')}`
    );
  });

  return summaryParts.length ? summaryParts.join(' | ') : 'None';
};

export default TurfSimulator;
